import type { SiteRagReport } from '../../types/siteRag';
import { createRegistryResolver, tierSource } from './resolveCore';
import type { RegistryResolver, ResolverFactoryOptions } from './types';

export interface NarrativeResolverInput extends ResolverFactoryOptions {
  reports?: SiteRagReport[];
  authoredOverrides?: Record<string, string>;
  llmNarratives?: Record<string, string>;
}

export function createNarrativeResolver(opts: NarrativeResolverInput): RegistryResolver {
  const fieldSources: Record<string, ReturnType<typeof tierSource>[]> = {};

  for (const report of opts.reports ?? []) {
    if (report.summary) {
      fieldSources[`${report.id}:summary`] = [
        tierSource(2, report.summary, {
          kind: 'report',
          ref: `report:${report.id}:summary`,
          label: `${report.reportType ?? 'report'} summary`,
        }),
      ];
    }
    const insights = report.insights;
    if (insights) {
      for (const [key, text] of Object.entries(insights)) {
        if (!text) continue;
        fieldSources[`${report.id}:insights.${key}`] = [
          tierSource(2, text, {
            kind: 'report',
            ref: `report:${report.id}:insights.${key}`,
            label: `Insight: ${key}`,
          }),
        ];
      }
    }
  }

  for (const [key, text] of Object.entries(opts.authoredOverrides ?? {})) {
    fieldSources[key] = [
      tierSource(1, text, {
        kind: 'manual',
        ref: `authored:${key}`,
        label: 'User-authored narrative',
      }),
    ];
  }

  for (const [key, text] of Object.entries(opts.llmNarratives ?? {})) {
    const sources = fieldSources[key] ?? [];
    sources.push(
      tierSource(3, text, {
        kind: 'llm-inference',
        ref: `llm:narrative:${key}`,
        label: 'LLM-generated narrative',
      }),
    );
    fieldSources[key] = sources;
  }

  return createRegistryResolver({
    registryId: 'narrative',
    siteId: opts.siteId,
    fieldSources,
    now: opts.now,
  });
}
